import { useState, useEffect, useCallback } from "react";


export const useFetchProfile = (userId, communityId, token, signal) => {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!token) {
      console.log('[useGetProfile.js] Skipping fetch: no token');
      return;
    }
    setIsLoading(true);
    try {
      const requestBody = { userId, communityId };
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` // <-- Add token here
        },
        body: JSON.stringify(requestBody),
        signal: signal,
      };
      const url = `${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfileToDisplay`;
      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[useGetProfile.js] HTTP error! Status: ${response.status}, Body: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setProfile(data);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log('[useGetProfile.js] Fetch aborted:', error.message);
      } else {
        console.error('[useGetProfile.js] Error fetching profile:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, communityId, token, signal]);

  useEffect(() => {
    console.log('[useGetProfile.js] useEffect triggered, dependencies:', {
      userId: userId || 'undefined',
      communityId: communityId || 'undefined',
      hasSignal: !!signal
    });

    if (!userId || !communityId) {
      console.log('[useGetProfile.js] Skipping fetch - missing required parameters:', {
        missingUserId: !userId,
        missingCommunityId: !communityId
      });
      setProfile(null);
      setIsLoading(false);
      return;
    }

    fetchProfile();

    return () => {
      console.log('[useGetProfile.js] Cleanup - aborting fetch for:', { 
        userId: userId || 'undefined', 
        communityId: communityId || 'undefined' 
      });
      if (signal) {
        signal.abort();
      }
    };
  }, [fetchProfile, signal, userId, communityId, token]);

  console.log('[useGetProfile.js] Returning profile state:', {
    isLoading,
    profileExists: !!profile,
    profileData: profile || 'null'
  });

  return { profile, isLoading };
};

export const useFetchProfiles = (userIds, communityId, signal) => {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  console.log('[useGetProfile.js] useFetchProfiles initialized with:', {
    userIds: userIds || 'undefined',
    communityId: communityId || 'undefined',
    hasSignal: !!signal
  });

  const fetchProfiles = useCallback(async () => {
    console.log('[useGetProfile.js] Starting profiles fetch for:', { 
      userIds: userIds || 'undefined', 
      communityId: communityId || 'undefined' 
    });
    
    setIsLoading(true);
    try {
      const requestBody = {
        userIds: userIds,
        communityId: communityId,
      };

      console.log('[useGetProfile.js] Request body:', JSON.stringify(requestBody));

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
        signal: signal,
      };

      const url = `${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfilesToDisplay`;
      console.log('[useGetProfile.js] Fetching from URL:', url);

      const response = await fetch(url, requestOptions);
      console.log('[useGetProfile.js] Response received:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[useGetProfile.js] HTTP error! Status: ${response.status}, Body: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[useGetProfile.js] Profiles data received:', JSON.stringify(data));
      setProfiles(data);

    } catch (error) {
      if (error.name === "AbortError") {
        console.log('[useGetProfile.js] Fetch aborted:', error.message);
      } else {
        console.error('[useGetProfile.js] Error fetching profiles:', error);
      }
    } finally {
      console.log('[useGetProfile.js] Fetch completed for:', { 
        userIds: userIds || 'undefined', 
        communityId: communityId || 'undefined' 
      });
      setIsLoading(false);
    }
  }, [userIds, communityId, signal]);

  useEffect(() => {
    console.log('[useGetProfile.js] useEffect triggered, dependencies:', {
      userIds: userIds || 'undefined',
      communityId: communityId || 'undefined',
      hasSignal: !!signal
    });

    if (!userIds?.length || !communityId) {
      console.log('[useGetProfile.js] Skipping fetch - missing required parameters:', {
        missingUserIds: !userIds?.length,
        missingCommunityId: !communityId
      });
      setProfiles([]);
      setIsLoading(false);
      return;
    }

    fetchProfiles();

    return () => {
      console.log('[useGetProfile.js] Cleanup - aborting fetch for:', { 
        userIds: userIds || 'undefined', 
        communityId: communityId || 'undefined' 
      });
      if (signal) {
        signal.abort();
      }
    };
  }, [fetchProfiles, signal, userIds, communityId]);

  console.log('[useGetProfile.js] Returning profiles state:', {
    isLoading,
    profileCount: profiles.length,
    profileIds: profiles.map(p => p._id)
  });

  return { profiles, isLoading };
};


//2025
// 
// // import { useState, useEffect, useCallback } from "react";

// /**
//  * Custom hook to fetch a single user profile.
//  *
//  * @param {string} communityId - The ID of the community to which the profile belongs.
//  * @param {AbortSignal} signal - An AbortSignal to optionally abort the fetch request.
//  * @returns {Object} An object containing the fetched profiles and a loading state.
//  */
// export const useFetchProfile = (userId, communityId, signal) => {
//   const [profile, setProfile] = useState(null); // Changed from profiles to profile and default state to null
//   const [isLoading, setIsLoading] = useState(false);

//   const fetchProfile = useCallback(async () => {
//     setIsLoading(true);
//     try {
//       const requestBody = {
//         userId: userId,
//         communityId: communityId,
//       };

//       const requestOptions = {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(requestBody),
//         signal: signal,
//       };

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfileToDisplay`,
//         requestOptions
//       );
//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }
//       const data = await response.json();
//       setProfile(data);
//     } catch (error) {
//       if (error.name === "AbortError") {
//         console.log("Fetch aborted:", error);
//       } else {
//         console.error("Error fetching profile:", error);
//       }
//     } finally {
//       setIsLoading(false);
//     }
//   }, [userId, communityId, signal]);

//   useEffect(() => {
//     fetchProfile();
//     // Cleanup function to abort fetch on component unmount
//     return () => {
//       if (signal) {
//         signal.abort();
//       }
//     };
//   }, [fetchProfile, signal]);

//   return { profile, isLoading }; // Changed from profiles to profile
// };

// /**
//  * Custom hook to fetch multiple user profiles.
//  *
//  * @param {Array<string>} userIds - The IDs of the users to fetch profiles for.
//  * @param {string} communityId - The ID of the community to which the profiles belong.
//  * @param {AbortSignal} signal - An AbortSignal to optionally abort the fetch request.
//  * @returns {Object} An object containing the fetched profiles and a loading state.
//  */
// export const useFetchProfiles = (userIds, communityId, signal) => {
//   const [profiles, setProfiles] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);

//   const fetchProfiles = useCallback(async () => {
//     setIsLoading(true);
//     try {
//       const requestBody = {
//         userIds: userIds,
//         communityId: communityId,
//       };

//       const requestOptions = {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(requestBody),
//         signal: signal,
//       };

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfilesToDisplay`,
//         requestOptions
//       );
//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }
//       const data = await response.json();
//       setProfiles(data);
//     } catch (error) {
//       if (error.name === "AbortError") {
//         console.log("Fetch aborted:", error);
//       } else {
//         console.error("Error fetching profiles:", error);
//       }
//     } finally {
//       setIsLoading(false);
//     }
//   }, [userIds, communityId, signal]);

//   useEffect(() => {
//     fetchProfiles();
//     // Cleanup function to abort fetch on component unmount
//     return () => {
//       if (signal) {
//         signal.abort();
//       }
//     };
//   }, [fetchProfiles, signal]);

//   return { profiles, isLoading };
// };
